import React, { useState } from 'react';
import { 
  Box, 
  Stepper, 
  Step, 
  StepLabel, 
  StepButton,
  Button,
  IconButton,
  useTheme
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckIcon from '@mui/icons-material/Check';
import { tokens } from '../theme';

/**
 * A reusable card stepper component for multi-step forms
 * 
 * @param {Object} props - Component props
 * @param {Array} props.steps - Array of step labels
 * @param {number} props.activeStep - Current active step
 * @param {Function} props.setActiveStep - Function to set active step
 * @param {Object} props.completed - Object of completed steps
 * @param {Function} props.setCompleted - Function to set completed steps
 * @param {Function} props.handleNext - Function to handle next step
 * @param {Function} props.handleBack - Function to handle previous step
 * @param {Function} props.handleComplete - Function to handle step completion
 * @param {boolean} [props.disableNext] - Disable the Next button
 * @param {React.ReactNode} props.children - Step content
 */
const CardStepper = ({ 
  steps,
  activeStep,
  setActiveStep,
  completed,
  setCompleted,
  handleNext,
  handleBack,
  handleComplete,
  children,
  disableNext = false
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  // If step handlers aren't provided, create default ones
  const totalSteps = () => steps.length;
  const completedSteps = () => Object.keys(completed || {}).length;
  const isLastStep = () => activeStep === totalSteps() - 1;
  const allStepsCompleted = () => completedSteps() === totalSteps();

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const defaultHandleNext = () => {
    const newActiveStep = isLastStep() && !allStepsCompleted()
      ? steps.findIndex((step, i) => !(i in completed))
      : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const defaultHandleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const defaultHandleComplete = () => {
    const newCompleted = completed ? { ...completed } : {};
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    
    if (!allStepsCompleted()) {
      defaultHandleNext();
    }
  };

  // Use provided handlers or defaults
  const onNext = handleNext || defaultHandleNext;
  const onBack = handleBack || defaultHandleBack;
  const onComplete = handleComplete || defaultHandleComplete;

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper nonLinear activeStep={activeStep} sx={{ mb: 3 }}>
        {steps.map((label, index) => (
          <Step key={label} completed={completed?.[index]}>
            <StepButton color="inherit" onClick={handleStep(index)}>
              <StepLabel>{label}</StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Box>
        {/* Step Content */}
        {children}

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          {activeStep !== 0 ? (
            <IconButton
              onClick={onBack}
              sx={{
                background: colors.primary[400],
                color: '#fff',
                borderRadius: '50%',
                boxShadow: '0 4px 16px #b7eaff33',
                '&:hover': { background: colors.primary[600], boxShadow: '0 6px 24px #4db6d644' },
                width: 48,
                height: 48,
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>
          ) : (
            <Box sx={{ width: 48 }} />
          )}

          {activeStep !== steps.length - 1 ? (
            <IconButton
              onClick={onNext}
              disabled={Boolean(disableNext)}
              sx={{
                background: colors.primary[400],
                color: '#fff',
                borderRadius: '50%',
                boxShadow: '0 4px 16px #b7eaff33',
                '&:hover': { background: colors.primary[600], boxShadow: '0 6px 24px #4db6d644' },
                width: 48,
                height: 48,
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          ) : (
            <Box sx={{ width: 48 }} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default CardStepper;
